import { faInfoCircle, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Button, Box, Text } from '@mantine/core'
import { useEffect, useState } from 'react'

import { Trans, useTranslation } from '@flecto/translations'
import {
  type CoreProduct,
  type CoreBundleItemPayload,
} from '@flecto/types'
import { Icon } from '@flecto/ui'

import { useModalBundleItemsLocation } from '../../../bundles'
import { useModalBundleItemsSelection } from '../../hooks/useModalBundleItemsSelection'
import { useFormContext } from './FormBundle'
import TableBundleItems from './TableBundleItems'

export function TabItems() {
  const form = useFormContext()
  const { t } = useTranslation('saas', { keyPrefix: 'items' })
  const { openModal: openItemsSelectionModal } = useModalBundleItemsSelection()
  const { openModal: openBundleItemsLocationModal } =
    useModalBundleItemsLocation()
  const bundleItems =
    form.values.bundle_items.filter((item) => !item._destroy) || []
  const [itemsWithDifferentAddress, setItemsWithDifferentAddress] = useState<
    CoreBundleItemPayload[]
  >([])

  const handleItemsUpdate = (
    items: Array<
      CoreProduct & {
        quantity: number
      }
    >
  ) => {
    const currentBundleItems = form.values.bundle_items
    const bundleAddressReferenceId =
      form.values.bundle_items?.[0]?.owner_address?.id ??
      items?.[0]?.owner_address?.id ??
      undefined
    const allNewItemsHaveSameAddress = items.every(
      (item) => item.owner_address?.id === bundleAddressReferenceId
    )

    const newBundleItems = items.map((item) => ({
      allow_overbooking: !item.item_attributes?.allow_overbooking,
      has_bundles_associated: true,
      main_photo: item.main_photo,
      quantity: item.quantity,
      quantity_available: item.item_attributes?.quantity_available,
      title: item.title,
      item_id: item.id,
      discount_amount: 0,
      track_quantity: item.item_attributes?.track_quantity,
      owner_address: item.owner_address,
      _destroy: false,
    }))

    if (!allNewItemsHaveSameAddress) {
      setItemsWithDifferentAddress(newBundleItems)
      return
    }

    const categoryId = items?.[0].category_id

    form.setFieldValue(
      'bundle_items',
      currentBundleItems.concat(newBundleItems)
    )

    if (!form.values.category_id) {
      form.setFieldValue('category_id', categoryId)
      form.values.category_id = categoryId
    }
  }

  useEffect(() => {
    if (!itemsWithDifferentAddress.length) return
    setTimeout(() => {
      openBundleItemsLocationModal({ bundleItems: itemsWithDifferentAddress })
      setItemsWithDifferentAddress([])
    }, 0)
  }, [itemsWithDifferentAddress, openBundleItemsLocationModal])

  return (
    <Box>
      <Text c='gray.7' mb='lg'>
        <Trans i18nKey='new_bundle_items_notice' t={t} />
      </Text>
      <Button
        mb='md'
        variant='outline'
        onClick={() =>
          openItemsSelectionModal({
            handleItemsUpdate,
            itemsInBundle: bundleItems,
          })
        }
        leftSection={<Icon icon={faPlus} />}
      >
        {t('add_products_button')}
      </Button>
      <TableBundleItems bundleItems={bundleItems} />
      <Flex gap='md' align='flex-start' py='md'>
        <Icon
          icon={faInfoCircle}
          size='md'
          color='gray.5'
          style={{ flexShrink: 0 }}
        />
        <Text c='gray.7' size='sm'>
          {t('new_bundle_items_footer')}
        </Text>
      </Flex>
    </Box>
  )
}
